/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search, Filter, XCircle, ChevronDown } from "lucide-react";
import { useContext } from "react";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";
import { generateCaregiverCVPDF } from "../../../utils/caregiverCv";
import ResetPasswordModal from "../../../components/ResetPasswordModal";
import WorkingDetailsModal from "../../../components/caregivers/WorkingDetailsModal";
import DeleteConfirmationModal from "../../../components/caregivers/DeleteConfirmationModal";
import VettingHistoryModal from "../../../components/caregivers/VettingHistoryModal";
import WorkHistoryModal from "../../../components/caregivers/WorkHistoryModal";
import CaregiverTable from "../../../components/caregivers/CaregiverTable";
import BankDetailsModal from "../../../components/caregivers/BankDetailsModal";

const VerifiedCareGivers = () => {
  const {
    verifiedCareGivers,
    verifiedCareGiversPagination,
    getVerifiedCareGivers,
    changeAvailablity,
    toggleCareGiverStatus,
    deleteCareGiverById,
    getCaregiverVettingHistory,
    resetCareGiverPassword,
    getCaregiverWorkingDetails,
    getCaregiverWorkHistory,
    getCaregiverCVData,
    aToken,
    backendUrl,
    serviceCategories,
  } = useContext(AdminContext);

  // Filters
  const [nameSearch, setNameSearch] = useState("");
  const [phoneSearch, setPhoneSearch] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const handleSearch = () => {
    setNameSearch(nameInput);
    setPhoneSearch(phoneInput);
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };
  const [subcityFilter, setSubcityFilter] = useState("");
  const [workingStatusFilter, setWorkingStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [downloadingCV, setDownloadingCV] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Dropdown states
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubcityDropdown, setShowSubcityDropdown] = useState(false);
  const [showWorkingStatusDropdown, setShowWorkingStatusDropdown] =
    useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // UI State
  const [expandedRow, setExpandedRow] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  // Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caregiverToDelete, setCaregiverToDelete] = useState(null);
  const [caregiverToDeleteName, setCaregiverToDeleteName] = useState("");

  const [showVettingHistory, setShowVettingHistory] = useState(false);
  const [vettingHistory, setVettingHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [caregiverToReset, setCaregiverToReset] = useState(null);
  const [caregiverResetData, setCaregiverResetData] = useState(null);

  const [showWorkingModal, setShowWorkingModal] = useState(false);
  const [selectedCaregiverForWorking, setSelectedCaregiverForWorking] =
    useState(null);
  const [workingDetails, setWorkingDetails] = useState(null);
  const [loadingWorkingDetails, setLoadingWorkingDetails] = useState(false);

  const [showWorkHistoryModal, setShowWorkHistoryModal] = useState(false);
  const [selectedCaregiverForHistory, setSelectedCaregiverForHistory] =
    useState(null);

  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
  const [selectedCaregiverForBank, setSelectedCaregiverForBank] =
    useState(null);

  // Category options - use dynamic categories from context
  const categories = [
    { id: "", title: "All Categories" },
    ...serviceCategories.map((cat) => ({
      id: cat.id,
      title: cat.label,
    })),
  ];

  // Subcity options
  const SUBCITIES = [
    "Addis Ketema",
    "Akaki Kaliti",
    "Arada",
    "Bole",
    "Gullele",
    "Kirkos",
    "Kolfe Keranio",
    "Lideta",
    "Nifas Silk-Lafto",
    "Yeka",
    "Lemi Kura",
  ];

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      const options = {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };
      return date.toLocaleDateString("en-US", options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Track whether the initial load has already fired
  const isFirstRender = useRef(true);

  // Fetch caregivers with server-side filtering and pagination
  const fetchVerifiedCaregivers = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        await getVerifiedCareGivers({
          page: page,
          limit: itemsPerPage,
          name: nameSearch,
          phone: phoneSearch,
          category: categoryFilter === "" ? "" : categoryFilter,
          subcity: subcityFilter,
          workingStatus: workingStatusFilter,
        });
      } catch (error) {
        console.error("Fetch error:", error);
        toast.error("Failed to fetch verified caregivers");
      } finally {
        setIsLoading(false);
      }
    },
    [
      getVerifiedCareGivers,
      itemsPerPage,
      nameSearch,
      phoneSearch,
      categoryFilter,
      subcityFilter,
      workingStatusFilter,
    ],
  );

  // Initial load — runs once on mount
  useEffect(() => {
    fetchVerifiedCaregivers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch effect for filter/pagination changes (skips first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    fetchVerifiedCaregivers(currentPage);
  }, [
    currentPage,
    itemsPerPage,
    nameSearch,
    phoneSearch,
    categoryFilter,
    subcityFilter,
    workingStatusFilter,
    fetchVerifiedCaregivers,
  ]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    nameSearch,
    phoneSearch,
    categoryFilter,
    subcityFilter,
    workingStatusFilter,
  ]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".relative")) {
        setShowCategoryDropdown(false);
        setShowSubcityDropdown(false);
        setShowWorkingStatusDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Handle CV download
  const handleDownloadCV = async (caregiverId, caregiverName) => {
    setActiveMenu(null);
    setDownloadingCV(caregiverId);

    try {
      const cvData = await getCaregiverCVData(caregiverId);

      if (!cvData) {
        toast.error("No caregiver data found");
        return;
      }

      const extractedFields = extractFieldValues(cvData.fieldValues || []);

      // Map backend data structure to CV format
      const mappedData = {
        _id: cvData.id || caregiverId,
        name: cvData.name,
        email: cvData.email,
        phoneNumber: cvData.phoneNumber,
        image: cvData.image,
        gender: cvData.gender,
        age: cvData.dob
          ? new Date().getFullYear() - new Date(cvData.dob).getFullYear()
          : null,
        nationality: "Ethiopian", // Default if not in data
        subcity: cvData.subcity,
        category:
          cvData.serviceCategory?.label ||
          cvData.serviceCategory?.name ||
          "Caregiver",
        experience: cvData.experience,
        languages: cvData.languages || [],
        date_time_preference_to_work: cvData.workTimePreference || "Flexible",
        fees: cvData.fees,
        rating: cvData.rating,
        about: cvData.about,

        // Category-specific fields from fieldValues (fallback first)
        ...extractedFields.values,

        // Keep database labels available for the PDF's dynamic field formatter.
        _fieldLabels: extractedFields.labels,

        // Category-specific fields from profileSnapshot (priority - overwrites fieldValues)
        ...(cvData.profileSnapshot || {}),

        // Education is stored on the caregiver record, not only in the snapshot.
        education:
          cvData.education || cvData.profileSnapshot?.education || "Not specified",
      };

      await generateCaregiverCVPDF(mappedData);
      toast.success(`CV for ${caregiverName} downloaded successfully!`);
    } catch (error) {
      console.error("Download error:", error);
      console.error("Error stack:", error.stack);
      toast.error(`Failed to generate CV PDF: ${error.message}`);
    } finally {
      setDownloadingCV(null);
    }
  };

  // Helper function to extract field values from EAV structure
  const extractFieldValues = (fieldValues) => {
    const extracted = {};
    const labels = {};

    fieldValues.forEach((fv) => {
      const key = fv.field?.key;
      if (!key) return;

      // The structure has 'values' array, not 'value'
      let value = fv.values || fv.value;

      // If it's an array with one item, extract it
      if (Array.isArray(value) && value.length === 1) {
        value = value[0];
      }

      // Parse JSON values if needed
      try {
        if (
          typeof value === "string" &&
          (value.startsWith("[") || value.startsWith("{"))
        ) {
          value = JSON.parse(value);
        }
      } catch {
        // Keep as string if not valid JSON
      }

      extracted[key] = value;
      if (fv.field?.label) labels[key] = fv.field.label;
    });

    return { values: extracted, labels };
  };

  // Handle reset password for caregiver
  const handleResetPasswordClick = (caregiver) => {
    setCaregiverToReset(caregiver.id);
    setCaregiverResetData({
      name: caregiver.name,
      email: caregiver.email,
      phoneNumber: caregiver.phone,
      image: caregiver.avatar,
    });
    setShowResetPasswordModal(true);
    setActiveMenu(null);
  };

  const handleResetCaregiverPassword = async () => {
    if (!caregiverToReset) return;

    await resetCareGiverPassword(caregiverToReset);
    setShowResetPasswordModal(false);
  };

  // Handle view working details
  const handleViewWorkingDetails = async (caregiverId) => {
    setActiveMenu(null);
    setSelectedCaregiverForWorking(caregiverId);
    setLoadingWorkingDetails(true);

    try {
      const details = await getCaregiverWorkingDetails(caregiverId);
      if (details.success) {
        setWorkingDetails(details);
        setShowWorkingModal(true);
      } else {
        toast.error("No working details available");
      }
    } catch (error) {
      console.error("Error fetching working details:", error);
      toast.error("Failed to fetch working details");
    } finally {
      setLoadingWorkingDetails(false);
    }
  };

  // Handle view work history
  const handleViewWorkHistory = async (caregiverId) => {
    setActiveMenu(null);
    setSelectedCaregiverForHistory(caregiverId);
    setShowWorkHistoryModal(true);
  };

  // Map API data to component format
  const mappedCaregivers = Array.isArray(verifiedCareGivers)
    ? verifiedCareGivers.map((c, i) => {
        // Debug: Log the raw data to see what we're receiving
        if (i === 0) {
          console.log("Raw caregiver data sample:", {
            languages: c?.languages,
            about: c?.about,
            workTimePreference: c?.workTimePreference,
            experience: c?.experience,
          });
        }

        return {
          id: c?.id || `CG-${1000 + i}`,
          name: c?.name || "Unknown",
          avatar: c?.image || null,
          phone: c?.phoneNumber || "N/A",
          email: c?.email || "N/A",
          category:
            c?.serviceCategory?.label ||
            c?.serviceCategoryId ||
            c?.category ||
            "",
          fees: c?.fees ? Number(c.fees) : 0,
          rating: Number(c?.rating) || 0,
          vettingStatus: c?.vettingStatus || c?.vetting_status || "Onboarded",
          isAvailable: c?.isAvailable ?? c?.available ?? false,
          isBooked: c?.isBooked ?? c?.is_booked ?? false,
          isActive: c?.status === "active",
          isWorking: c?.isWorking || false,
          experience: c?.experience ? `${c.experience} Years` : "Not specified",
          nationality: "Ethiopian", // Default as it's not in the database
          languages: Array.isArray(c?.languages)
            ? c.languages
            : typeof c?.languages === "string"
              ? c.languages.split(", ")
              : [],
          education: "Not specified", // Not in database, would need to be added
          about: c?.about || "No description provided.",
          hasCourse: c?.hasCourse ?? false,
          courseDetails: c?.courseDetails || "",
          courseDetails_amh: c?.courseDetails_amh || "",
          hasReferee: c?.hasReferee ?? false,
          refereeName: c?.refereeName || "",
          refereePhone: c?.refereePhone || "",
          workPreferences: c?.workTimePreference
            ? c.workTimePreference
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())
            : "Not specified",
          createdAt: c?.createdAt,
          onboardedAt: c?.onboardedAt,
          subcity: c?.subcity || "",
          addressLine1: c?.addressLine1 || "",
          addressLine2: c?.addressLine2 || "",
        };
      })
    : [];

  const handleToggleStatus = async (id, field) => {
    try {
      if (field === "isAvailable") {
        await changeAvailablity(id);
      } else if (field === "isActive") {
        await toggleCareGiverStatus(id);
      }
      // Refetch after toggle to update list
      fetchVerifiedCaregivers(currentPage);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteClick = (id, name) => {
    setCaregiverToDelete(id);
    setCaregiverToDeleteName(name);
    setShowDeleteModal(true);
    setActiveMenu(null);
  };

  const confirmDelete = async () => {
    if (caregiverToDelete) {
      try {
        const result = await deleteCareGiverById(caregiverToDelete);
        if (result.success) {
          toast.success(result.message || "Caregiver deleted successfully");
          setShowDeleteModal(false);
          setCaregiverToDelete(null);
          setCaregiverToDeleteName("");
          // Refetch after delete
          fetchVerifiedCaregivers(currentPage);
        } else {
          toast.error(result.message || "Failed to delete caregiver");
        }
      } catch (error) {
        toast.error("Failed to delete caregiver");
      }
    }
  };

  // Handle viewing vetting history
  const handleViewVettingHistory = async (caregiverId) => {
    setLoadingHistory(true);
    try {
      const history = await getCaregiverVettingHistory(caregiverId);
      setVettingHistory(history || []);
      setShowVettingHistory(true);
    } catch (error) {
      toast.error("Failed to fetch vetting history");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleViewBankDetails = (caregiverId) => {
    setSelectedCaregiverForBank(caregiverId);
    setShowBankDetailsModal(true);
  };

  // Reset all filters
  const resetFilters = () => {
    setNameInput("");
    setPhoneInput("");
    setNameSearch("");
    setPhoneSearch("");
    setCategoryFilter("");
    setSubcityFilter("");
    setWorkingStatusFilter("");
    setCurrentPage(1);
  };

  // Format category for display
  const formatCategory = (category) => {
    return categories.find((c) => c.id === category)?.title || category;
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            Verified Caregivers
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm sm:text-base">
            List of verified caregiver profiles.
            {verifiedCareGiversPagination && (
              <span className="ml-2 font-semibold text-primary">
                ({verifiedCareGiversPagination.total} total)
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Filters Toggle Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold transition-all ${
            showFilters
              ? "bg-primary text-white dark:bg-primary dark:text-white hover:bg-primary/90 dark:hover:bg-primary/90"
              : "bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700"
          }`}
        >
          <Filter size={18} />
          <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
        </button>
      </div>

      {/* Filters Section */}
      <div
        className={`bg-card dark:bg-dark-surface p-4 rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border mb-6 transition-all duration-300 ${
          showFilters ? "block" : "hidden"
        }`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 items-center">
          {/* Name Search */}
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-primary"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by name..."
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={handleKeyPress}
              className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 font-medium transition-all"
            />
          </div>

          {/* Phone Search */}
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-primary"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by phone number..."
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              onKeyDown={handleKeyPress}
              className="w-full pl-12 pr-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 font-medium transition-all"
            />
          </div>

          {/* Search & Reset Buttons */}
          <div className="flex gap-2 justify-end lg:col-span-2">
            <button
              onClick={handleSearch}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all bg-primary hover:bg-primary/90 text-white w-full md:w-auto"
            >
              <Search size={18} />
              <span>Search</span>
            </button>
            <button
              onClick={resetFilters}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all bg-gray-50 dark:bg-gray-900 text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 w-full md:w-auto"
            >
              <XCircle size={18} />
              <span>Reset Filters</span>
            </button>
          </div>
        </div>

        {/* Category, Subcity, and Working Status Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          {/* Category Dropdown */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Category
            </label>
            <div className="relative">
              <button
                onClick={() => {
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setShowSubcityDropdown(false);
                  setShowWorkingStatusDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border-none text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>
                  {categoryFilter
                    ? categories.find((c) => c.id === categoryFilter)?.title ||
                      "All Categories"
                    : "All Categories"}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showCategoryDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showCategoryDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setCategoryFilter(category.id);
                        setShowCategoryDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        categoryFilter === category.id
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {category.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Subcity Dropdown */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Subcity
            </label>
            <div className="relative">
              <button
                onClick={() => {
                  setShowSubcityDropdown(!showSubcityDropdown);
                  setShowCategoryDropdown(false);
                  setShowWorkingStatusDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border-none text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>{subcityFilter || "All Subcities"}</span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showSubcityDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showSubcityDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                  <button
                    onClick={() => {
                      setSubcityFilter("");
                      setShowSubcityDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      subcityFilter === ""
                        ? "bg-primary text-white dark:bg-primary"
                        : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                    }`}
                  >
                    All Subcities
                  </button>
                  {SUBCITIES.map((subcity) => (
                    <button
                      key={subcity}
                      onClick={() => {
                        setSubcityFilter(subcity);
                        setShowSubcityDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        subcityFilter === subcity
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {subcity}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Working Status Dropdown */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Working Status
            </label>
            <div className="relative">
              <button
                onClick={() => {
                  setShowWorkingStatusDropdown(!showWorkingStatusDropdown);
                  setShowCategoryDropdown(false);
                  setShowSubcityDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border-none text-sm font-medium text-text dark:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span>
                  {workingStatusFilter === "working"
                    ? "Working"
                    : workingStatusFilter === "not-working"
                      ? "Not Working"
                      : "All Status"}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-primary transition-transform ${
                    showWorkingStatusDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showWorkingStatusDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                  <button
                    onClick={() => {
                      setWorkingStatusFilter("");
                      setShowWorkingStatusDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      workingStatusFilter === ""
                        ? "bg-primary text-white dark:bg-primary"
                        : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                    }`}
                  >
                    All Status
                  </button>
                  <button
                    onClick={() => {
                      setWorkingStatusFilter("working");
                      setShowWorkingStatusDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      workingStatusFilter === "working"
                        ? "bg-primary text-white dark:bg-primary"
                        : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                    }`}
                  >
                    Working
                  </button>
                  <button
                    onClick={() => {
                      setWorkingStatusFilter("not-working");
                      setShowWorkingStatusDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      workingStatusFilter === "not-working"
                        ? "bg-primary text-white dark:bg-primary"
                        : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                    }`}
                  >
                    Not Working
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table Component */}
      <CaregiverTable
        caregivers={mappedCaregivers}
        pagination={verifiedCareGiversPagination}
        isLoading={isLoading}
        showVettingColumn={false}
        showWorkingColumn={true}
        showUploadButton={false}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onToggleStatus={handleToggleStatus}
        onDeleteClick={handleDeleteClick}
        onResetPasswordClick={handleResetPasswordClick}
        onViewVettingHistory={handleViewVettingHistory}
        onViewWorkingDetails={handleViewWorkingDetails}
        onViewWorkHistory={handleViewWorkHistory}
        onViewBankDetails={handleViewBankDetails}
        onDownloadCV={handleDownloadCV}
        downloadingCV={downloadingCV}
        expandedRow={expandedRow}
        onToggleExpand={setExpandedRow}
        activeMenu={activeMenu}
        onSetActiveMenu={setActiveMenu}
        formatCategory={formatCategory}
        formatDate={formatDate}
      />

      {/* Modals */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCaregiverToDelete(null);
          setCaregiverToDeleteName("");
        }}
        onConfirm={confirmDelete}
        caregiverName={caregiverToDeleteName}
      />

      <VettingHistoryModal
        isOpen={showVettingHistory}
        onClose={() => setShowVettingHistory(false)}
        vettingHistory={vettingHistory}
        loadingHistory={loadingHistory}
      />

      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        onConfirm={handleResetCaregiverPassword}
        userData={caregiverResetData}
      />

      {/* Working Details Modal */}
      {showWorkingModal && (
        <WorkingDetailsModal
          caregiverId={selectedCaregiverForWorking}
          workingDetails={workingDetails}
          loading={loadingWorkingDetails}
          onClose={() => setShowWorkingModal(false)}
        />
      )}

      {/* Work History Modal */}
      {showWorkHistoryModal && (
        <WorkHistoryModal
          isOpen={showWorkHistoryModal}
          onClose={() => {
            setShowWorkHistoryModal(false);
            setSelectedCaregiverForHistory(null);
          }}
          caregiverId={selectedCaregiverForHistory}
          onFetchHistory={getCaregiverWorkHistory}
        />
      )}

      <BankDetailsModal
        isOpen={showBankDetailsModal}
        onClose={() => {
          setShowBankDetailsModal(false);
          setSelectedCaregiverForBank(null);
          fetchVerifiedCaregivers(currentPage);
        }}
        caregiverId={selectedCaregiverForBank}
      />
    </div>
  );
};

export default VerifiedCareGivers;
